// financeCalculators.js - Financial Calculator Utilities

// Regular SIP Calculator (Monthly SIP)
export function calculateSIP({ sip, finalAmount, durationYears, rate = 12 }) {
  const r = rate / 100 / 12; // monthly rate
  const n = durationYears * 12; // total months

  if (sip) {
    const futureValue = sip * (((1 + r) ** n - 1) * (1 + r)) / r;
    return { futureValue: parseFloat(futureValue.toFixed(2)) };
  } else if (finalAmount) {
    const sipRequired = finalAmount * r / (((1 + r) ** n - 1) * (1 + r));
    return { sipRequired: parseFloat(sipRequired.toFixed(2)) };
  } else {
    return { error: "Provide either SIP or Final Amount" };
  }
}

// Step-up SIP Calculator (Annual Step-up)
export function calculateStepUpSIP({ initialSIP, finalAmount, durationYears, rate = 12, stepUpPercent = 10 }) {
  const r = rate / 100 / 12; // monthly rate
  const n = durationYears * 12; // total months
  const stepUp = stepUpPercent / 100;

  if (initialSIP) {
    let total = 0;
    for (let i = 0; i < n; i++) {
      const yearIndex = Math.floor(i / 12); // step-up annually
      const sip_i = initialSIP * ((1 + stepUp) ** yearIndex);
      total += sip_i * ((1 + r) ** (n - i));
    }
    return { futureValue: parseFloat(total.toFixed(2)) };
  } else {
    return { error: "Reverse calculation for Step-up SIP is not supported yet" };
  }
}

// SWP Calculator (Monthly Withdrawal)
export function calculateSWP({ swp, corpus, durationYears, rate = 12 }) {
  const r = rate / 100 / 12; // monthly rate
  const n = durationYears * 12; // total months

  if (swp) {
    const corpusRequired = swp * ((1 - (1 + r) ** -n) / r);
    return { corpusRequired: parseFloat(corpusRequired.toFixed(2)) };
  } else if (corpus) {
    const swpAmount = corpus * r / (1 - (1 + r) ** -n);
    return { swpAmount: parseFloat(swpAmount.toFixed(2)) };
  } else {
    return { error: "Provide either SWP or Corpus" };
  }
}

// Future Value Calculator
export function calculateFutureValue({ initialInvestment = 0, annualContribution = 0, returnRate = 12, durationYears = 10 }) {
  const r = returnRate / 100; // annual rate as decimal
  const n = durationYears; // number of years

  // Future value of lump sum
  const futureValueLumpSum = initialInvestment * (1 + r) ** n;

  // Future value of annuity (annual contributions)
  const futureValueAnnuity = annualContribution > 0 ?
    annualContribution * (((1 + r) ** n - 1) / r) : 0;

  const totalFutureValue = futureValueLumpSum + futureValueAnnuity;

  return {
    futureValue: parseFloat(totalFutureValue.toFixed(2)),
    lumpSumGrowth: parseFloat(futureValueLumpSum.toFixed(2)),
    annuityGrowth: parseFloat(futureValueAnnuity.toFixed(2))
  };
}

// Tier 1 city appreciation data
const appreciationRates = {
  "Mumbai": { "2025": 5.9, "2026": 6.2, "2027": 6.5, "2028": 6.7, "2029": 6.9, "2030": 7.0, "2031": 7.2, "2032": 7.3, "2033": 7.5, "2034": 7.6 },
  "Delhi NCR": { "2025": 2.9, "2026": 4.5, "2027": 5.0, "2028": 5.5, "2029": 6.0, "2030": 6.2, "2031": 6.5, "2032": 6.7, "2033": 6.9, "2034": 7.0 },
  "Bangalore": { "2025": 13.1, "2026": 9.0, "2027": 8.5, "2028": 8.0, "2029": 7.5, "2030": 7.2, "2031": 7.0, "2032": 6.8, "2033": 6.5, "2034": 6.2 },
  "Hyderabad": { "2025": 4.8, "2026": 5.5, "2027": 6.0, "2028": 6.2, "2029": 6.5, "2030": 6.7, "2031": 6.9, "2032": 7.0, "2033": 7.2, "2034": 7.5 },
  "Pune": { "2025": 6.8, "2026": 7.0, "2027": 7.2, "2028": 7.4, "2029": 7.5, "2030": 7.6, "2031": 7.7, "2032": 7.8, "2033": 8.0, "2034": 8.2 },
  "Chennai": { "2025": 9.0, "2026": 8.5, "2027": 8.0, "2028": 7.5, "2029": 7.2, "2030": 7.0, "2031": 6.8, "2032": 6.6, "2033": 6.5, "2034": 6.3 },
  "Kolkata": { "2025": 9.6, "2026": 8.0, "2027": 7.5, "2028": 7.0, "2029": 6.5, "2030": 6.2, "2031": 6.0, "2032": 5.8, "2033": 5.5, "2034": 5.2 },
  "Ahmedabad": { "2025": 6.1, "2026": 6.3, "2027": 6.5, "2028": 6.7, "2029": 6.9, "2030": 7.0, "2031": 7.2, "2032": 7.3, "2033": 7.5, "2034": 7.6 }
};

function getAppreciationRates(city, startYear, durationYears) {
  const cityRates = appreciationRates[city];
  const rates = [];
  for (let i = 0; i < durationYears; i++) {
    const year = (startYear + i).toString();
    rates.push(cityRates?.[year] || 5.0); // default fallback
  }
  return rates;
}

// Rent vs Buy Calculator
export function calculateRentVsBuy({
  city,
  monthlyRent,
  annualRentIncreasePercent,
  homePrice,
  downPaymentPercent,
  loanInterestRate,
  loanTenureYears,
  investmentReturnRate,
  startYear = 2025
}) {
  const months = loanTenureYears * 12;
  const downPayment = homePrice * (downPaymentPercent / 100);
  const loanAmount = homePrice - downPayment;
  const monthlyInterestRate = loanInterestRate / 100 / 12;

  const emi = loanAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, months) /
    (Math.pow(1 + monthlyInterestRate, months) - 1);

  let homeValue = homePrice;
  const appreciation = getAppreciationRates(city, startYear, loanTenureYears);
  for (let rate of appreciation) {
    homeValue *= (1 + rate / 100);
  }

  let investedAmount = downPayment;
  let currentRent = monthlyRent;
  const yearlyData = [];

  for (let i = 0; i < months; i++) {
    const monthlyReturn = investmentReturnRate / 100 / 12;
    const monthlySavings = Math.max(emi - currentRent, 0);
    investedAmount = (investedAmount + monthlySavings) * (1 + monthlyReturn);

    if ((i + 1) % 12 === 0) {
      const year = Math.floor(i / 12) + 1;
      yearlyData.push({
        year,
        investmentValue: parseFloat(investedAmount.toFixed(2)),
        homeValue: parseFloat((homePrice * Math.pow(1 + appreciation[year - 1] / 100, year)).toFixed(2)),
        totalRentPaid: parseFloat((currentRent * 12 * year).toFixed(2))
      });
      currentRent *= (1 + annualRentIncreasePercent / 100);
    }
  }

  return {
    emi: parseFloat(emi.toFixed(2)),
    totalInvestedValue: parseFloat(investedAmount.toFixed(2)),
    finalHomeValue: parseFloat(homeValue.toFixed(2)),
    downPayment: parseFloat(downPayment.toFixed(2)),
    yearlyData,
    recommendation: investedAmount > homeValue ? 'Renting may be better' : 'Buying may be better'
  };
}

// Generate SWP projection data for charts
export function generateSWPProjection({ corpus, swp, durationYears, rate = 12 }) {
  const r = rate / 100 / 12; // monthly rate
  const months = durationYears * 12;
  let remainingCorpus = corpus;
  const projectionData = [];

  for (let i = 0; i <= months; i++) {
    if (i > 0) {
      remainingCorpus = remainingCorpus * (1 + r) - swp;
      if (remainingCorpus < 0) remainingCorpus = 0;
    }

    if (i % 12 === 0) {
      projectionData.push({
        year: i / 12,
        remainingCorpus: parseFloat(remainingCorpus.toFixed(2)),
        totalWithdrawn: parseFloat((swp * i).toFixed(2))
      });
    }
  }

  return projectionData;
}

// Retirement Calculator – JS port of your XLS logic with guardrails.
export function calculateRetirementFundingGap(input) {
  // --- Helpers --------------------------------------------------------------
  const toDate = (d) => (d instanceof Date ? d : new Date(d));
  const normRate = (r) => (r > 1 ? r / 100 : r);         // accept 5 or 0.05
  const clamp = (x, lo, hi) => Math.min(Math.max(x, lo), hi);
  const isFiniteNumber = (x) => typeof x === "number" && Number.isFinite(x);

  // Actual/365 to match Excel-like behavior in your sheet
  const yearsBetweenA365 = (d1, d2) => {
    const ms = toDate(d2).getTime() - toDate(d1).getTime();
    return ms / (1000 * 60 * 60 * 24 * 365);
  };

  const addYearsA365 = (d, years) => {
    const ms = toDate(d).getTime() + years * 365 * 24 * 3600 * 1000;
    return new Date(ms);
  };

  const round2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;

  // --- Read & normalize inputs ---------------------------------------------
  const dob = toDate(input.dateOfBirth);                       // e.g., "1987-01-01"
  const currentDate = input.currentDate ? toDate(input.currentDate) : new Date();

  const retirementAge = Number(input.retirementAgeYears);
  const lifeExpectancy = Number(input.lifeExpectancyYears);

  const inflation = normRate(Number(input.inflation));
  const currentMonthlyExpense = Number(input.currentMonthlyExpense);

  const retWork = normRate(Number(input.expectedReturnWorking));
  const retRet = normRate(Number(input.expectedReturnRetired));

  const reductionPct = normRate(Number(input.expenseReductionPct));
  const currentSavings = Number(input.currentSavings || 0);
  const retirementBenefitsLumpSum = Number(input.retirementBenefitsLumpSum || 0);

  // Optional behavior flags
  const options = {
    // When true, compute retirement date exactly as: currentDate + yearsToRetirement * 365 days
    // This reproduces the date style seen in your sheet (e.g., "12/16/2039").
    retirementDateFromYears: input?.options?.retirementDateFromYears ?? true,

    // When set, it *overrides* years-to-retirement used for expense inflation only.
    // You can use this to match a legacy sheet if it used a different time basis.
    inflationYearsOverride: input?.options?.inflationYearsOverride ?? null,
  };

  // --- Basic validations / range checks (mirrors your sheet's ranges) -------
  if (!(dob instanceof Date) || Number.isNaN(dob.getTime())) {
    return { error: "Invalid dateOfBirth" };
  }
  if (retirementAge < 35 || retirementAge > 65) {
    return { error: "retirementAgeYears must be in [35, 65]" };
  }
  if (lifeExpectancy < 65 || lifeExpectancy > 95) {
    return { error: "lifeExpectancyYears must be in [65, 95]" };
  }
  if (!isFiniteNumber(inflation) || inflation < 0) return { error: "Invalid inflation" };
  if (!isFiniteNumber(retWork) || retWork < 0) return { error: "Invalid expectedReturnWorking" };
  if (!isFiniteNumber(retRet) || retRet < 0) return { error: "Invalid expectedReturnRetired" };
  if (!isFiniteNumber(reductionPct) || reductionPct < 0 || reductionPct >= 1) {
    return { error: "expenseReductionPct must be in [0, 1)" };
  }
  if (!isFiniteNumber(currentMonthlyExpense) || currentMonthlyExpense < 0) {
    return { error: "currentMonthlyExpense must be >= 0" };
  }

  // --- Core calculations ----------------------------------------------------
  // Current age (fractional, Actual/365 to match sheet)
  const currentAgeYears = yearsBetweenA365(dob, currentDate);

  // Years to retirement
  const yearsToRetirement = retirementAge - currentAgeYears;

  // Retirement date:
  //  • Sheet-like behavior (currentDate + yearsToRet * 365 days)
  //  • Or exact DOB-based date: dob + retirementAge years (toggle options.retirementDateFromYears=false)
  const retirementDate = options.retirementDateFromYears
    ? addYearsA365(currentDate, yearsToRetirement)
    : new Date(dob.getFullYear() + retirementAge, dob.getMonth(), dob.getDate());

  // Current annual expense
  const currentAnnualExpense = currentMonthlyExpense * 12;

  // Expense at retirement (inflate to retirement, then apply reduction)
  const inflationYears = options.inflationYearsOverride ?? yearsToRetirement;
  const monthlyAtRetirement = currentMonthlyExpense * Math.pow(1 + inflation, inflationYears);
  const monthlyAfterReduction = monthlyAtRetirement * (1 - reductionPct);
  const annualAtRetirement = monthlyAfterReduction * 12;

  // Years in retirement
  const yearsInRetirement = Math.max(0, lifeExpectancy - retirementAge);

  // PV of a growing annuity due at retirement (payments grow at inflation; discount at retired return)
  // Standard formula: PV = P1 * [1 - ((1+g)/(1+i))^n] / (i - g), where:
  //  P1 = first year's expense in retirement (annualAtRetirement), i = retRet, g = inflation, n = yearsInRetirement
  const i = retRet;
  const g = inflation;

  let pvNeededAtRetirement;
  if (Math.abs(i - g) < 1e-12) {
    // Limit case: if i ≈ g, PV reduces to P1 * n
    pvNeededAtRetirement = annualAtRetirement * yearsInRetirement;
  } else {
    const ratio = Math.pow((1 + g) / (1 + i), yearsInRetirement);
    pvNeededAtRetirement = annualAtRetirement * (1 - ratio) / (i - g);
  }

  // FV of current savings at retirement (compounding during working years)
  const fvSavingsAtRetirement = currentSavings * Math.pow(1 + retWork, Math.max(0, yearsToRetirement));

  // Total funds & funding gap
  const totalFundsAtRetirement = retirementBenefitsLumpSum + fvSavingsAtRetirement;
  const fundingGapAtRetirement = pvNeededAtRetirement - totalFundsAtRetirement;

  // --- Format the result ----------------------------------------------------
  return {
    // Inputs echoed (normalized)
    inputs: {
      dateOfBirth: dob.toISOString().slice(0, 10),
      currentDate: currentDate.toISOString(),
      retirementAgeYears: retirementAge,
      lifeExpectancyYears: lifeExpectancy,
      inflation,
      expectedReturnWorking: retWork,
      expectedReturnRetired: retRet,
      expenseReductionPct: reductionPct,
      currentMonthlyExpense,
      currentSavings,
      retirementBenefitsLumpSum,
    },

    // Calculated
    currentAgeYears: round2(currentAgeYears),
    currentAnnualExpense: round2(currentAnnualExpense),
    retirementDate: retirementDate.toISOString().slice(0, 10),
    yearsToRetirement: round2(yearsToRetirement),
    monthlyExpensesAtRetirement: round2(monthlyAtRetirement),
    monthlyExpensesAfterReduction: round2(monthlyAfterReduction),
    annualExpensesAtRetirement: round2(annualAtRetirement),
    yearsInRetirement,
    pvOfAnnuityNeededAtRetirement: round2(pvNeededAtRetirement),
    fvSavingsAtRetirement: round2(fvSavingsAtRetirement),
    totalFundsAtRetirement: round2(totalFundsAtRetirement),

    // Key output
    fundingGapAtRetirement: round2(fundingGapAtRetirement),
    hasSurplus: fundingGapAtRetirement < 0,

    // For reconciliation
    optionsUsed: {
      retirementDateFromYears: !!options.retirementDateFromYears,
      inflationYearsUsed: Number(inflationYears.toFixed(6)),
    },
  };
}
